import { useCallback, useEffect, useState } from "react";
import { T, base } from "../lib/theme";
import { apiFetch } from "../lib/api";

// ── Stage definitions ──────────────────────────────────────────────────────
const STAGES = [
  { id: "new_order",            label: "New Orders",          color: "#64748b", icon: "🛒" },
  { id: "pending_confirmation", label: "Pending Confirmation", color: "#f59e0b", icon: "⏳" },
  { id: "confirmed",            label: "Confirmed Orders",    color: "#22c55e", icon: "✅" },
  { id: "follow_up",            label: "Follow-Up",           color: "#38bdf8", icon: "🔔" },
  { id: "cancelled",            label: "Cancelled Orders",    color: "#f43f5e", icon: "❌" },
];
const STAGE_MAP = Object.fromEntries(STAGES.map(s => [s.id, s]));

const DIAGNOSTIC_ICONS = {
  webhook_received:  "📡",
  order_parsed:      "📋",
  ai_triggered:      "🤖",
  whatsapp_sent:     "💬",
  response_received: "📩",
  status_updated:    "🔄",
  follow_up_sent:    "🔔",
  pipeline_moved:    "➡️",
};

const CURRENCIES = [
  { code: "",    label: "Auto (from order)" },
  { code: "PKR", label: "PKR — Pakistani Rupee" },
  { code: "USD", label: "USD — US Dollar" },
  { code: "AED", label: "AED — UAE Dirham" },
  { code: "GBP", label: "GBP — British Pound" },
  { code: "EUR", label: "EUR — Euro" },
  { code: "SAR", label: "SAR — Saudi Riyal" },
  { code: "CAD", label: "CAD — Canadian Dollar" },
  { code: "AUD", label: "AUD — Australian Dollar" },
  { code: "INR", label: "INR — Indian Rupee" },
  { code: "TRY", label: "TRY — Turkish Lira" },
];

// ── Date presets (like Shopify / Ads Manager) ──────────────────────────────
const DATE_PRESETS = [
  { label: "All time",    value: "all" },
  { label: "Today",       value: "today" },
  { label: "Yesterday",   value: "yesterday" },
  { label: "Last 3 days", value: "3d" },
  { label: "Last 7 days", value: "7d" },
  { label: "Last 30 days",value: "30d" },
  { label: "Last 3 months",value:"3m" },
  { label: "Last year",   value: "1y" },
];

function presetToRange(preset) {
  const now  = new Date();
  const sod  = new Date(now); sod.setHours(0,0,0,0);   // start of today
  const eod  = new Date(now); eod.setHours(23,59,59,999);
  switch (preset) {
    case "today":
      return { dateFrom: sod.toISOString(), dateTo: eod.toISOString() };
    case "yesterday": {
      const yS = new Date(sod); yS.setDate(yS.getDate() - 1);
      const yE = new Date(eod); yE.setDate(yE.getDate() - 1);
      return { dateFrom: yS.toISOString(), dateTo: yE.toISOString() };
    }
    case "3d": {
      const s = new Date(sod); s.setDate(s.getDate() - 2);
      return { dateFrom: s.toISOString(), dateTo: eod.toISOString() };
    }
    case "7d": {
      const s = new Date(sod); s.setDate(s.getDate() - 6);
      return { dateFrom: s.toISOString(), dateTo: eod.toISOString() };
    }
    case "30d": {
      const s = new Date(sod); s.setDate(s.getDate() - 29);
      return { dateFrom: s.toISOString(), dateTo: eod.toISOString() };
    }
    case "3m": {
      const s = new Date(sod); s.setMonth(s.getMonth() - 3);
      return { dateFrom: s.toISOString(), dateTo: eod.toISOString() };
    }
    case "1y": {
      const s = new Date(sod); s.setFullYear(s.getFullYear() - 1);
      return { dateFrom: s.toISOString(), dateTo: eod.toISOString() };
    }
    default: return {};  // "all"
  }
}

// ── Utilities ──────────────────────────────────────────────────────────────
function timeAgo(ts) {
  if (!ts) return "";
  const diff = Math.floor((Date.now() - new Date(ts)) / 1000);
  if (diff < 60)    return `${diff}s ago`;
  if (diff < 3600)  return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  return `${Math.floor(diff / 86400)}d ago`;
}

// ── Sub-components ─────────────────────────────────────────────────────────
const StatCard = ({ label, value, color, sub }) => (
  <div style={{
    background: T.bg1, border: `1px solid ${T.border1}`,
    borderRadius: 14, padding: "14px 18px", minWidth: 120, flex: 1,
  }}>
    <div style={{ fontSize: 22, fontWeight: 800, color, letterSpacing: "-0.03em" }}>{value}</div>
    <div style={{ fontSize: 11, fontWeight: 600, color: T.text2, marginTop: 2 }}>{label}</div>
    {sub && <div style={{ fontSize: 10, color: T.text3, marginTop: 1 }}>{sub}</div>}
  </div>
);

const Badge = ({ children, color }) => (
  <span style={{
    background: `${color}22`, color, border: `1px solid ${color}44`,
    borderRadius: 6, padding: "2px 8px", fontSize: 11, fontWeight: 600,
  }}>{children}</span>
);

// ── Order Card ─────────────────────────────────────────────────────────────
const OrderCard = ({ order, stageColor, onSelect, selected, onDragStart, onDragEnd, isDragging, selectMode, isChecked, onToggle }) => {
  const isSelected  = !selectMode && selected?._id === order._id;
  const statusColor = order.confirmationStatus === "confirmed" ? "#22c55e"
    : order.confirmationStatus === "cancelled" ? "#f43f5e"
    : order.confirmationStatus === "no_response" ? "#f59e0b"
    : "#64748b";

  const handleClick = () => {
    if (selectMode) onToggle();
    else onSelect(order);
  };

  return (
    <div
      draggable={!selectMode}
      onDragStart={!selectMode ? onDragStart : undefined}
      onDragEnd={!selectMode ? onDragEnd : undefined}
      onClick={handleClick}
      style={{
        background: isChecked ? "rgba(99,102,241,0.10)" : isSelected ? `${stageColor}18` : T.bg1,
        border: `1px solid ${isChecked ? "#6366f1" : isSelected ? stageColor : T.border1}`,
        borderRadius: 12, padding: "12px 14px",
        cursor: selectMode ? "pointer" : "grab",
        marginBottom: 8, transition: "all 0.15s",
        opacity: isDragging ? 0.4 : 1,
        boxShadow: isDragging ? "none" : isChecked ? "0 0 0 2px rgba(99,102,241,0.2)" : undefined,
        position: "relative",
      }}
    >
      {/* Checkbox overlay in select mode */}
      {selectMode && (
        <div style={{
          position: "absolute", top: 10, right: 10,
          width: 17, height: 17, borderRadius: 5,
          background: isChecked ? "#6366f1" : T.bg0,
          border: `2px solid ${isChecked ? "#6366f1" : T.border2}`,
          display: "flex", alignItems: "center", justifyContent: "center",
          zIndex: 2, transition: "all 0.15s", flexShrink: 0,
        }}>
          {isChecked && <svg width="9" height="9" viewBox="0 0 12 12" fill="none"><polyline points="2,6 5,9 10,3" stroke="#fff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/></svg>}
        </div>
      )}
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 8, paddingRight: selectMode ? 22 : 0 }}>
        <div>
          <div style={{ fontSize: 13, fontWeight: 700, color: T.text1 }}>
            {order.orderData?.customerName || "Unknown Customer"}
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: 5, marginTop: 3 }}>
            <span style={{ fontSize: 10, color: T.text3 }}>#{order.orderId}</span>
            <span style={{
              fontSize: 9, fontWeight: 700, padding: "1px 6px", borderRadius: 4,
              background: order.platform === "shopify" ? "rgba(150,191,72,0.15)" : "rgba(150,88,246,0.15)",
              color: order.platform === "shopify" ? "#5e8e3e" : "#8b5cf6",
              border: `1px solid ${order.platform === "shopify" ? "rgba(150,191,72,0.3)" : "rgba(150,88,246,0.3)"}`,
              textTransform: "uppercase", letterSpacing: "0.04em",
            }}>
              {order.platform === "shopify" ? "Shopify" : "WooCommerce"}
            </span>
          </div>
        </div>
        <div style={{ fontSize: 13, fontWeight: 700, color: stageColor, whiteSpace: "nowrap" }}>
          {order.orderData?.currency} {order.orderData?.total}
        </div>
      </div>
      <div style={{ display: "flex", alignItems: "center", gap: 6, marginTop: 8 }}>
        <Badge color={statusColor}>
          {order.confirmationStatus || "pending"}
        </Badge>
        <span style={{ fontSize: 10, color: T.text3 }}>
          {timeAgo(order.createdAt)}
        </span>
      </div>
    </div>
  );
};

// ── Diagnostic Viewer ──────────────────────────────────────────────────────
const DiagnosticViewer = ({ orderId, token }) => {
  const [steps,   setSteps]   = useState([]);
  const [loading, setLoading] = useState(false);
  const [orderInfo, setOrderInfo] = useState(null);

  const load = useCallback(async () => {
    if (!orderId) return;
    setLoading(true);
    try {
      const d = await apiFetch(`/api/ecom-pipeline/orders/${orderId}/diagnostics`, {}, token);
      setSteps(d.diagnostics || []);
      setOrderInfo({ orderId: d.orderId, platform: d.platform });
    } catch { setSteps([]); }
    setLoading(false);
  }, [orderId, token]);

  useEffect(() => { load(); }, [load]);

  if (loading) return <div style={{ padding: 24, color: T.text2, textAlign: "center" }}>Loading diagnostics…</div>;
  if (!steps.length) return <div style={{ padding: 24, color: T.text3, textAlign: "center" }}>No diagnostic steps recorded yet.</div>;

  return (
    <div style={{ padding: "8px 0" }}>
      {orderInfo && (
        <div style={{ fontSize: 12, color: T.text2, marginBottom: 12, paddingLeft: 8 }}>
          Order #{orderInfo.orderId} · {orderInfo.platform}
        </div>
      )}
      {steps.map((step, i) => {
        const isOk   = step.status === "success";
        const isErr  = step.status === "error";
        const isSkip = step.status === "skipped";
        const color  = isOk ? "#22c55e" : isErr ? "#f43f5e" : isSkip ? "#64748b" : "#f59e0b";
        const icon   = isOk ? "✅" : isErr ? "❌" : isSkip ? "⏭️" : "⏳";
        return (
          <div key={i} style={{
            display: "flex", gap: 12, padding: "10px 12px",
            borderLeft: `3px solid ${color}`, marginBottom: 6,
            background: `${color}08`, borderRadius: "0 8px 8px 0",
          }}>
            <span style={{ fontSize: 16 }}>{DIAGNOSTIC_ICONS[step.step] || "🔹"}</span>
            <div style={{ flex: 1 }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                <span style={{ fontSize: 12, fontWeight: 700, color: T.text1 }}>
                  {icon} {step.step?.replace(/_/g, " ").replace(/\b\w/g, c => c.toUpperCase())}
                </span>
                <span style={{ fontSize: 10, color: T.text3 }}>
                  {step.timestamp ? new Date(step.timestamp).toLocaleTimeString() : ""}
                </span>
              </div>
              {step.message && <div style={{ fontSize: 11, color: T.text2, marginTop: 3 }}>{step.message}</div>}
              {step.error   && <div style={{ fontSize: 11, color: "#f43f5e", background: "rgba(244,63,94,0.08)", borderRadius: 4, padding: "4px 8px", marginTop: 4 }}>{step.error}</div>}
            </div>
          </div>
        );
      })}
    </div>
  );
};

// ── Settings Panel ─────────────────────────────────────────────────────────
const SettingsPanel = ({ token, showToast }) => {
  const [settings,  setSettings]  = useState(null);
  const [saving,    setSaving]    = useState(false);
  const [activeTab, setActiveTab] = useState("general"); // general | followups | kb | currency

  const load = useCallback(async () => {
    try {
      const d = await apiFetch("/api/ecom-pipeline/settings", {}, token);
      setSettings(d.settings);
    } catch { showToast?.("Failed to load settings", "error"); }
  }, [token]);

  useEffect(() => { load(); }, [load]);

  const save = async () => {
    setSaving(true);
    try {
      await apiFetch("/api/ecom-pipeline/settings", {
        method: "POST", body: JSON.stringify(settings),
      }, token);
      showToast?.("Settings saved");
    } catch { showToast?.("Failed to save settings", "error"); }
    setSaving(false);
  };

  const updateFollowUp = (i, field, val) => {
    setSettings(s => {
      const fus = [...(s.followUps || [])];
      fus[i] = { ...fus[i], [field]: val };
      return { ...s, followUps: fus };
    });
  };

  if (!settings) return <div style={{ padding: 24, color: T.text2, textAlign: "center" }}>Loading settings…</div>;

  const inp = {
    width: "100%", background: T.bg0, border: `1px solid ${T.border2}`,
    borderRadius: 8, color: T.text1, padding: "8px 12px",
    fontSize: 13, outline: "none", boxSizing: "border-box",
    fontFamily: base.font,
  };
  const lbl = { fontSize: 12, fontWeight: 600, color: T.text2, marginBottom: 4, display: "block" };
  const tog = (on) => ({
    width: 40, height: 22, borderRadius: 11,
    background: on ? "#6366f1" : T.bg3,
    position: "relative", cursor: "pointer", flexShrink: 0,
    transition: "background 0.2s", border: `1px solid ${on ? "#6366f1" : T.border2}`,
  });
  const knob = (on) => ({
    position: "absolute", top: 2, left: on ? 18 : 2,
    width: 16, height: 16, borderRadius: "50%",
    background: "#fff", transition: "left 0.2s",
  });

  const tabBtn = (id, label) => (
    <button
      key={id}
      onClick={() => setActiveTab(id)}
      style={{
        padding: "6px 14px", borderRadius: 8, fontSize: 12, fontWeight: 600,
        cursor: "pointer", border: "none",
        background: activeTab === id ? "rgba(99,102,241,0.15)" : "transparent",
        color: activeTab === id ? "#6366f1" : T.text3,
      }}
    >
      {label}
    </button>
  );

  return (
    <div style={{ display: "flex", flexDirection: "column", height: "100%" }}>
      {/* Settings sub-tabs */}
      <div style={{
        display: "flex", gap: 4, padding: "10px 16px",
        borderBottom: `1px solid ${T.border1}`, flexShrink: 0,
      }}>
        {tabBtn("general",  "General")}
        {tabBtn("followups","Follow-Ups")}
        {tabBtn("kb",       "Knowledge Base")}
        {tabBtn("currency", "Currency")}
      </div>

      <div style={{ flex: 1, overflowY: "auto", padding: "16px 20px" }}>

        {/* ── GENERAL TAB ── */}
        {activeTab === "general" && (
          <>
            <div style={{ marginBottom: 20 }}>
              <label style={lbl}>Confirmation Message Template</label>
              <div style={{ fontSize: 11, color: T.text3, marginBottom: 6 }}>
                Placeholders: {"{customerName}"} {"{items}"} {"{total}"} {"{address}"} {"{orderId}"}
              </div>
              <textarea
                rows={7}
                value={settings.confirmationTemplate || ""}
                onChange={e => setSettings(s => ({ ...s, confirmationTemplate: e.target.value }))}
                style={{ ...inp, resize: "vertical", fontFamily: "monospace", fontSize: 12 }}
              />
            </div>

            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 16 }}>
              <div>
                <div style={{ fontSize: 13, fontWeight: 600, color: T.text1 }}>AI Intent Detection</div>
                <div style={{ fontSize: 11, color: T.text3 }}>Use AI to detect YES/NO from natural language</div>
              </div>
              <div style={tog(settings.useAIIntentDetection)} onClick={() => setSettings(s => ({ ...s, useAIIntentDetection: !s.useAIIntentDetection }))}>
                <div style={knob(settings.useAIIntentDetection)} />
              </div>
            </div>

            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 16 }}>
              <div>
                <div style={{ fontSize: 13, fontWeight: 600, color: T.text1 }}>Auto-Cancel After No Response</div>
                <div style={{ fontSize: 11, color: T.text3 }}>Automatically cancel if customer never replies</div>
              </div>
              <div style={tog(settings.autoCancelEnabled)} onClick={() => setSettings(s => ({ ...s, autoCancelEnabled: !s.autoCancelEnabled }))}>
                <div style={knob(settings.autoCancelEnabled)} />
              </div>
            </div>

            {settings.autoCancelEnabled && (
              <div style={{ marginBottom: 16 }}>
                <label style={lbl}>Auto-Cancel After (minutes)</label>
                <input
                  type="number" min={60}
                  value={settings.autoCancelAfterMinutes || 2880}
                  onChange={e => setSettings(s => ({ ...s, autoCancelAfterMinutes: parseInt(e.target.value) || 2880 }))}
                  style={{ ...inp, width: 160 }}
                />
              </div>
            )}

            <div style={{ display: "flex", gap: 16, marginBottom: 20 }}>
              <div style={{ flex: 1 }}>
                <label style={lbl}>Confirmed → Platform Status</label>
                <input value={settings.confirmStatusMap || "processing"} onChange={e => setSettings(s => ({ ...s, confirmStatusMap: e.target.value }))} style={inp} />
              </div>
              <div style={{ flex: 1 }}>
                <label style={lbl}>Cancelled → Platform Status</label>
                <input value={settings.cancelStatusMap || "cancelled"} onChange={e => setSettings(s => ({ ...s, cancelStatusMap: e.target.value }))} style={inp} />
              </div>
            </div>
          </>
        )}

        {/* ── FOLLOW-UPS TAB ── */}
        {activeTab === "followups" && (
          <>
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 16 }}>
              <div>
                <div style={{ fontSize: 13, fontWeight: 600, color: T.text1 }}>Enable Follow-Ups</div>
                <div style={{ fontSize: 11, color: T.text3 }}>Auto-send reminders when customer doesn't respond</div>
              </div>
              <div style={tog(settings.followUpsEnabled)} onClick={() => setSettings(s => ({ ...s, followUpsEnabled: !s.followUpsEnabled }))}>
                <div style={knob(settings.followUpsEnabled)} />
              </div>
            </div>

            {settings.followUpsEnabled && (settings.followUps || []).map((fu, i) => (
              <div key={i} style={{ background: T.bg0, border: `1px solid ${T.border1}`, borderRadius: 10, padding: 14, marginBottom: 10 }}>
                <div style={{ fontSize: 12, fontWeight: 700, color: T.text2, marginBottom: 10 }}>Follow-Up #{i + 1}</div>
                <div style={{ marginBottom: 8 }}>
                  <label style={{ ...lbl, marginBottom: 2 }}>Delay (minutes after previous message)</label>
                  <input
                    type="number" min={1}
                    value={fu.delayMinutes || 30}
                    onChange={e => updateFollowUp(i, "delayMinutes", parseInt(e.target.value) || 30)}
                    style={{ ...inp, width: 120 }}
                  />
                </div>
                <div>
                  <label style={lbl}>Message</label>
                  <textarea
                    rows={3}
                    value={fu.message || ""}
                    onChange={e => updateFollowUp(i, "message", e.target.value)}
                    style={{ ...inp, resize: "vertical", fontSize: 12 }}
                  />
                </div>
              </div>
            ))}

            {!settings.followUpsEnabled && (
              <div style={{ color: T.text3, fontSize: 13, textAlign: "center", padding: 24 }}>
                Enable follow-ups to configure messages.
              </div>
            )}
          </>
        )}

        {/* ── KNOWLEDGE BASE TAB ── */}
        {activeTab === "kb" && (
          <>
            <div style={{
              background: "rgba(99,102,241,0.08)", border: "1px solid rgba(99,102,241,0.2)",
              borderRadius: 10, padding: "12px 14px", marginBottom: 16,
            }}>
              <div style={{ fontSize: 13, fontWeight: 700, color: "#6366f1", marginBottom: 4 }}>Ecom Knowledge Base</div>
              <div style={{ fontSize: 12, color: T.text2, lineHeight: 1.6 }}>
                Write information about your store — products, pricing, policies, FAQs, shipping details, return policy etc.
                When a customer asks a question during order confirmation (instead of replying YES/NO),
                the AI will answer using this knowledge base and then remind them to confirm their order.
              </div>
            </div>

            <div style={{ marginBottom: 8 }}>
              <label style={lbl}>Store Knowledge Base</label>
              <div style={{ fontSize: 11, color: T.text3, marginBottom: 6 }}>
                Write in plain text. This is separate from the Lead Gen knowledge base.
              </div>
              <textarea
                rows={16}
                placeholder={`Example:\nStore Name: My Online Store\nProducts: We sell handmade clothing for men and women.\nShipping: 3-5 business days within Pakistan, 7-14 days internationally.\nReturn Policy: 7-day returns for unused items.\nPayment: Cash on delivery and online payment accepted.\nContact: Call/WhatsApp 03001234567 for support.`}
                value={settings.ecomKnowledgeBase || ""}
                onChange={e => setSettings(s => ({ ...s, ecomKnowledgeBase: e.target.value }))}
                style={{ ...inp, resize: "vertical", fontSize: 12, lineHeight: 1.6 }}
              />
            </div>

            <div style={{ fontSize: 11, color: T.text3, marginTop: 4 }}>
              {(settings.ecomKnowledgeBase || "").length} characters
              {!settings.ecomKnowledgeBase?.trim() && (
                <span style={{ color: "#f59e0b", marginLeft: 8 }}>
                  No KB set — unclear customer replies will pass to the Lead Gen AI.
                </span>
              )}
            </div>
          </>
        )}

        {/* ── CURRENCY TAB ── */}
        {activeTab === "currency" && (
          <>
            <div style={{
              background: "rgba(99,102,241,0.08)", border: "1px solid rgba(99,102,241,0.2)",
              borderRadius: 10, padding: "12px 14px", marginBottom: 20,
            }}>
              <div style={{ fontSize: 13, fontWeight: 700, color: "#6366f1", marginBottom: 4 }}>Display Currency</div>
              <div style={{ fontSize: 12, color: T.text2, lineHeight: 1.6 }}>
                Choose which currency symbol is shown in the pipeline stats and revenue totals.
                This only affects the display — order amounts come from your store unchanged.
              </div>
            </div>

            <div style={{ marginBottom: 20 }}>
              <label style={lbl}>Currency</label>
              <select
                value={settings.displayCurrency || ""}
                onChange={e => setSettings(s => ({ ...s, displayCurrency: e.target.value }))}
                style={{ ...inp, cursor: "pointer" }}
              >
                {CURRENCIES.map(c => (
                  <option key={c.code} value={c.code}>{c.label}</option>
                ))}
              </select>
            </div>

            <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 10 }}>
              {CURRENCIES.filter(c => c.code).map(c => (
                <div
                  key={c.code}
                  onClick={() => setSettings(s => ({ ...s, displayCurrency: c.code }))}
                  style={{
                    padding: "14px 16px", borderRadius: 10, cursor: "pointer",
                    border: `1px solid ${settings.displayCurrency === c.code ? "#6366f1" : T.border1}`,
                    background: settings.displayCurrency === c.code ? "rgba(99,102,241,0.1)" : T.bg0,
                    transition: "all 0.15s",
                  }}
                >
                  <div style={{ fontSize: 16, fontWeight: 800, color: settings.displayCurrency === c.code ? "#6366f1" : T.text1, marginBottom: 4 }}>
                    {c.code}
                  </div>
                  <div style={{ fontSize: 10, color: T.text3 }}>{c.label.split(" — ")[1]}</div>
                </div>
              ))}
            </div>
          </>
        )}

        <button
          onClick={save}
          disabled={saving}
          style={{
            width: "100%", padding: "11px 0", marginTop: 20,
            background: saving ? T.bg3 : "linear-gradient(135deg,#6366f1,#4f46e5)",
            color: "#fff", border: "none", borderRadius: 10,
            fontSize: 14, fontWeight: 700, cursor: saving ? "default" : "pointer",
            fontFamily: base.font,
          }}
        >
          {saving ? "Saving…" : "Save Settings"}
        </button>
      </div>
    </div>
  );
};

// ── Order Detail Panel ─────────────────────────────────────────────────────
const OrderDetailPanel = ({ order, token, showToast, onStageChange, onDeleted }) => {
  const [activeTab, setActiveTab] = useState("details");
  const [moving,    setMoving]    = useState(false);
  const [deleting,  setDeleting]  = useState(false);
  const [confirmDel,setConfirmDel]= useState(false);

  const stageInfo = STAGE_MAP[order.pipelineStage] || STAGES[0];

  const moveStage = async (newStage) => {
    setMoving(true);
    try {
      await apiFetch(`/api/ecom-pipeline/orders/${order._id}/stage`, {
        method: "PATCH", body: JSON.stringify({ stage: newStage }),
      }, token);
      showToast?.(`Moved to ${STAGE_MAP[newStage]?.label}`);
      onStageChange?.();
    } catch { showToast?.("Failed to move stage", "error"); }
    setMoving(false);
  };

  const deleteOrder = async () => {
    setDeleting(true);
    try {
      await apiFetch(`/api/ecom-pipeline/orders/${order._id}`, { method: "DELETE" }, token);
      showToast?.("Order deleted");
      onDeleted?.();
    } catch { showToast?.("Failed to delete order", "error"); }
    setDeleting(false);
    setConfirmDel(false);
  };

  const tabStyle = (active) => ({
    padding: "6px 14px", borderRadius: 8, cursor: "pointer",
    fontSize: 12, fontWeight: 600, border: "none",
    background: active ? "rgba(99,102,241,0.15)" : "transparent",
    color: active ? "#6366f1" : T.text2,
  });

  return (
    <div style={{ display: "flex", flexDirection: "column", flex: 1, minHeight: 0 }}>
      {/* Header */}
      <div style={{ padding: "16px 20px", borderBottom: `1px solid ${T.border1}` }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 12 }}>
          <div>
            <div style={{ fontSize: 15, fontWeight: 800, color: T.text1 }}>
              {order.orderData?.customerName}
            </div>
            <div style={{ fontSize: 12, color: T.text3, marginTop: 2 }}>
              Order #{order.orderId} · {order.platform}
            </div>
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <div style={{
              background: `${stageInfo.color}22`, color: stageInfo.color,
              border: `1px solid ${stageInfo.color}44`,
              borderRadius: 8, padding: "4px 10px", fontSize: 12, fontWeight: 700,
            }}>
              {stageInfo.icon} {stageInfo.label}
            </div>
          </div>
        </div>

        {/* Tabs */}
        <div style={{ display: "flex", gap: 4 }}>
          {[["details", "Details"], ["diagnostics", "Diagnostics"], ["followups", "Follow-Ups"]].map(([id, label]) => (
            <button key={id} style={tabStyle(activeTab === id)} onClick={() => setActiveTab(id)}>
              {label}
            </button>
          ))}
        </div>
      </div>

      {/* Tab body */}
      <div style={{ flex: 1, overflowY: "auto" }}>
        {activeTab === "details" && (
          <div style={{ padding: "16px 20px" }}>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12, marginBottom: 16 }}>
              <div style={{ background: T.bg0, borderRadius: 10, padding: 12 }}>
                <div style={{ fontSize: 11, color: T.text3, marginBottom: 4 }}>TOTAL</div>
                <div style={{ fontSize: 18, fontWeight: 800, color: "#22c55e" }}>
                  {order.orderData?.currency} {order.orderData?.total}
                </div>
              </div>
              <div style={{ background: T.bg0, borderRadius: 10, padding: 12 }}>
                <div style={{ fontSize: 11, color: T.text3, marginBottom: 4 }}>STATUS</div>
                <div style={{ fontSize: 14, fontWeight: 700, color: T.text1 }}>
                  {order.confirmationStatus || "pending"}
                </div>
              </div>
            </div>

            {/* Customer info */}
            <div style={{ marginBottom: 12 }}>
              <div style={{ fontSize: 11, color: T.text3, marginBottom: 6, fontWeight: 600 }}>CUSTOMER</div>
              <div style={{ fontSize: 13, color: T.text1 }}>{order.orderData?.customerName}</div>
              <div style={{ fontSize: 12, color: T.text2 }}>{order.orderData?.phone}</div>
              {order.orderData?.address && (
                <div style={{ fontSize: 12, color: T.text2, marginTop: 2 }}>📍 {order.orderData.address}</div>
              )}
            </div>

            {/* Items */}
            {order.orderData?.items?.length > 0 && (
              <div style={{ marginBottom: 16 }}>
                <div style={{ fontSize: 11, color: T.text3, marginBottom: 6, fontWeight: 600 }}>ITEMS</div>
                {order.orderData.items.map((item, i) => (
                  <div key={i} style={{
                    display: "flex", justifyContent: "space-between",
                    padding: "6px 0", borderBottom: `1px solid ${T.border1}`,
                    fontSize: 13, color: T.text1,
                  }}>
                    <span>{item.name} ×{item.quantity}</span>
                    <span style={{ color: T.text2 }}>{order.orderData.currency} {item.price}</span>
                  </div>
                ))}
              </div>
            )}

            {/* Customer reply */}
            {order.customerResponse && (
              <div style={{ marginBottom: 16 }}>
                <div style={{ fontSize: 11, color: T.text3, marginBottom: 6, fontWeight: 600 }}>CUSTOMER REPLY</div>
                <div style={{
                  background: T.bg0, border: `1px solid ${T.border1}`,
                  borderRadius: 8, padding: "8px 12px", fontSize: 13, color: T.text1,
                }}>
                  "{order.customerResponse}"
                  {order.detectedIntent && (
                    <span style={{ marginLeft: 8, fontSize: 11, color: order.detectedIntent === "confirmed" ? "#22c55e" : "#f43f5e" }}>
                      → {order.detectedIntent}
                    </span>
                  )}
                </div>
              </div>
            )}

            {/* Manual stage move */}
            <div style={{ marginBottom: 20 }}>
              <div style={{ fontSize: 11, color: T.text3, marginBottom: 8, fontWeight: 600 }}>MOVE TO STAGE</div>
              <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
                {STAGES.filter(s => s.id !== order.pipelineStage).map(s => (
                  <button
                    key={s.id}
                    disabled={moving}
                    onClick={() => moveStage(s.id)}
                    style={{
                      background: `${s.color}18`, color: s.color,
                      border: `1px solid ${s.color}44`,
                      borderRadius: 8, padding: "5px 12px", cursor: "pointer",
                      fontSize: 12, fontWeight: 600, fontFamily: base.font,
                    }}
                  >
                    {s.icon} {s.label}
                  </button>
                ))}
              </div>
            </div>

            {/* Delete */}
            <div style={{ borderTop: `1px solid ${T.border1}`, paddingTop: 16 }}>
              {!confirmDel ? (
                <button
                  onClick={() => setConfirmDel(true)}
                  style={{
                    width: "100%", padding: "9px", borderRadius: 10,
                    background: "rgba(244,63,94,0.08)", color: "#f43f5e",
                    border: "1px solid rgba(244,63,94,0.25)",
                    fontSize: 13, fontWeight: 600, cursor: "pointer", fontFamily: base.font,
                  }}
                >
                  Delete Order
                </button>
              ) : (
                <div style={{ background: "rgba(244,63,94,0.08)", border: "1px solid rgba(244,63,94,0.25)", borderRadius: 10, padding: 14 }}>
                  <div style={{ fontSize: 13, fontWeight: 600, color: "#f43f5e", marginBottom: 10 }}>
                    Delete this order and its chat history?
                  </div>
                  <div style={{ display: "flex", gap: 8 }}>
                    <button onClick={deleteOrder} disabled={deleting}
                      style={{ flex: 1, padding: "8px", borderRadius: 8, background: "#f43f5e", color: "#fff", border: "none", fontSize: 13, fontWeight: 700, cursor: "pointer", fontFamily: base.font }}>
                      {deleting ? "Deleting…" : "Yes, Delete"}
                    </button>
                    <button onClick={() => setConfirmDel(false)}
                      style={{ flex: 1, padding: "8px", borderRadius: 8, background: T.bg2, color: T.text2, border: `1px solid ${T.border1}`, fontSize: 13, fontWeight: 600, cursor: "pointer", fontFamily: base.font }}>
                      Cancel
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        )}

        {activeTab === "diagnostics" && <DiagnosticViewer orderId={order._id} token={token} />}

        {activeTab === "followups" && (
          <div style={{ padding: "16px 20px" }}>
            {(!order.followUps || order.followUps.length === 0) ? (
              <div style={{ color: T.text3, fontSize: 13, textAlign: "center", marginTop: 20 }}>
                No follow-ups scheduled for this order.
              </div>
            ) : order.followUps.map((fu, i) => {
              const statusColor = fu.status === "sent" ? "#22c55e"
                : fu.status === "failed"    ? "#f43f5e"
                : fu.status === "cancelled" ? "#64748b"
                : "#f59e0b";
              return (
                <div key={i} style={{ background: T.bg0, border: `1px solid ${T.border1}`, borderRadius: 10, padding: 12, marginBottom: 10 }}>
                  <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 6 }}>
                    <span style={{ fontSize: 13, fontWeight: 700, color: T.text1 }}>Follow-Up #{i + 1}</span>
                    <Badge color={statusColor}>{fu.status}</Badge>
                  </div>
                  {fu.scheduledAt && <div style={{ fontSize: 11, color: T.text3 }}>Scheduled: {new Date(fu.scheduledAt).toLocaleString()}</div>}
                  {fu.sentAt      && <div style={{ fontSize: 11, color: "#22c55e" }}>Sent: {new Date(fu.sentAt).toLocaleString()}</div>}
                  {fu.message     && <div style={{ fontSize: 12, color: T.text2, marginTop: 6, padding: "6px 8px", background: T.bg1, borderRadius: 6 }}>{fu.message.slice(0, 120)}{fu.message.length > 120 ? "…" : ""}</div>}
                  {fu.error       && <div style={{ fontSize: 11, color: "#f43f5e", marginTop: 4 }}>Error: {fu.error}</div>}
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

// ── Main Page ──────────────────────────────────────────────────────────────
export default function EcomPipelinePage({ token, showToast, socket }) {
  const [stages,       setStages]       = useState({});
  const [stats,        setStats]        = useState({ total: 0, confirmed: 0, cancelled: 0, pending: 0, followUp: 0, totalRevenue: "0" });
  const [loading,      setLoading]      = useState(true);
  const [selected,     setSelected]     = useState(null);
  const [tab,          setTab]          = useState("pipeline");
  const [platform,     setPlatform]     = useState("");
  const [datePreset,   setDatePreset]   = useState("all");
  const [displayCur,   setDisplayCur]   = useState("");
  const [dragging,     setDragging]     = useState(null);  // { orderId, fromStage, order }
  const [dragOver,     setDragOver]     = useState(null);  // stageId being hovered
  const [moving,       setMoving]       = useState(false);
  // ── Multi-select ──────────────────────────────────────────────────
  const [selectMode,   setSelectMode]   = useState(false);
  const [selection,    setSelection]    = useState(new Set()); // Set of order._id
  const [bulkDeleting, setBulkDeleting] = useState(false);
  const [bulkMoving,   setBulkMoving]   = useState(false);
  const [bulkStage,    setBulkStage]    = useState("");

  // Load display currency from settings once
  useEffect(() => {
    apiFetch("/api/ecom-pipeline/settings", {}, token)
      .then(d => { if (d.settings?.displayCurrency) setDisplayCur(d.settings.displayCurrency); })
      .catch(() => {});
  }, [token]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (platform) params.set("platform", platform);
      const range = presetToRange(datePreset);
      if (range.dateFrom) params.set("dateFrom", range.dateFrom);
      if (range.dateTo)   params.set("dateTo",   range.dateTo);
      const d = await apiFetch(`/api/ecom-pipeline/orders?${params}`, {}, token);
      setStages(d.stages || {});
      setStats(d.stats   || {});
    } catch { showToast?.("Failed to load e-com pipeline", "error"); }
    setLoading(false);
  }, [token, platform, datePreset]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    if (!socket) return;
    const handler = () => load();
    socket.on("ecom_pipeline_update", handler);
    socket.on("pipeline_update", handler);
    return () => {
      socket.off("ecom_pipeline_update", handler);
      socket.off("pipeline_update", handler);
    };
  }, [socket, load]);

  // ── Page-level stage move (used by drag-drop) ────────────────────
  const moveOrderStage = async (orderId, fromStage, toStage) => {
    if (fromStage === toStage || moving) return;
    setMoving(true);
    try {
      await apiFetch(`/api/ecom-pipeline/orders/${orderId}/stage`, {
        method: "PATCH", body: JSON.stringify({ stage: toStage }),
      }, token);
      // Optimistic update
      setStages(prev => {
        const updated = { ...prev };
        const card = (updated[fromStage] || []).find(o => o._id === orderId);
        if (!card) return prev;
        updated[fromStage] = (updated[fromStage] || []).filter(o => o._id !== orderId);
        updated[toStage]   = [{ ...card, pipelineStage: toStage }, ...(updated[toStage] || [])];
        return updated;
      });
      if (selected?._id === orderId) setSelected(null);
      showToast?.(`Moved to ${STAGE_MAP[toStage]?.label}`);
    } catch { showToast?.("Failed to move order", "error"); }
    setMoving(false);
  };

  // ── Drag handlers ─────────────────────────────────────────────────
  const onDragStart = (order, fromStage) => setDragging({ orderId: order._id, fromStage, order });
  const onDragEnd   = () => { setDragging(null); setDragOver(null); };
  const onDragOver  = (e, stageId) => { e.preventDefault(); setDragOver(stageId); };
  const onDrop      = (e, toStage) => {
    e.preventDefault();
    if (dragging && dragging.fromStage !== toStage) {
      moveOrderStage(dragging.orderId, dragging.fromStage, toStage);
    }
    setDragging(null);
    setDragOver(null);
  };

  // ── All orders flat list (for select-all) ────────────────────────
  const allOrders = Object.values(stages).flat();

  const toggleSelect = (id) => {
    setSelection(prev => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };

  const enterSelectMode = () => { setSelectMode(true); setSelection(new Set()); setSelected(null); };
  const exitSelectMode  = () => { setSelectMode(false); setSelection(new Set()); setBulkStage(""); };

  const bulkDelete = async () => {
    if (!selection.size || bulkDeleting) return;
    setBulkDeleting(true);
    try {
      await Promise.all([...selection].map(id =>
        apiFetch(`/api/ecom-pipeline/orders/${id}`, { method: "DELETE" }, token).catch(() => {})
      ));
      showToast?.(`Deleted ${selection.size} order${selection.size > 1 ? "s" : ""}`);
      exitSelectMode();
      load();
    } catch { showToast?.("Bulk delete failed", "error"); }
    setBulkDeleting(false);
  };

  const bulkMove = async () => {
    if (!selection.size || !bulkStage || bulkMoving) return;
    setBulkMoving(true);
    try {
      await Promise.all([...selection].map(id =>
        apiFetch(`/api/ecom-pipeline/orders/${id}/stage`, { method: "PATCH", body: JSON.stringify({ stage: bulkStage }) }, token).catch(() => {})
      ));
      showToast?.(`Moved ${selection.size} order${selection.size > 1 ? "s" : ""} to ${STAGE_MAP[bulkStage]?.label}`);
      exitSelectMode();
      load();
    } catch { showToast?.("Bulk move failed", "error"); }
    setBulkMoving(false);
  };

  const tabBtn = (id, label) => (
    <button
      key={id}
      style={{
        padding: "7px 16px", borderRadius: 9, cursor: "pointer",
        fontSize: 13, fontWeight: 700, border: "none",
        background: tab === id ? "linear-gradient(135deg,#6366f1,#4f46e5)" : T.bg2,
        color: tab === id ? "#fff" : T.text2,
        fontFamily: base.font,
      }}
      onClick={() => setTab(id)}
    >
      {label}
    </button>
  );

  const filterBtn = (val, label, active) => (
    <button
      key={val}
      style={{
        padding: "5px 11px", borderRadius: 8, cursor: "pointer",
        fontSize: 12, fontWeight: 600, border: `1px solid ${active ? "#6366f1" : T.border1}`,
        background: active ? "rgba(99,102,241,0.12)" : "transparent",
        color: active ? "#6366f1" : T.text3,
        fontFamily: base.font, whiteSpace: "nowrap",
      }}
      onClick={val !== undefined ? () => setPlatform(val) : undefined}
    >
      {label}
    </button>
  );

  // Revenue display: use selected displayCurrency or fall back to what orders report
  const revenueLabel = displayCur
    ? `${displayCur} ${parseFloat(stats.totalRevenue).toLocaleString()}`
    : `${stats.totalRevenue}`;

  return (
    <div style={{ display: "flex", flexDirection: "column", height: "100%", gap: 0 }}>
      {/* ── Top bar ────────────────────────────────────────────────── */}
      <div style={{
        display: "flex", alignItems: "center", justifyContent: "space-between",
        padding: "10px 16px", borderBottom: `1px solid ${T.border1}`,
        background: T.bg1, flexShrink: 0, gap: 12, flexWrap: "wrap",
      }}>
        <div style={{ display: "flex", gap: 6 }}>
          {tabBtn("pipeline", "🛒 Pipeline")}
          {tabBtn("settings", "⚙️ Settings")}
        </div>

        {tab === "pipeline" && (
          <div style={{ display: "flex", gap: 6, alignItems: "center", flexWrap: "wrap" }}>
            {/* Platform filter */}
            <span style={{ fontSize: 11, color: T.text3 }}>Store:</span>
            {[["", "All"], ["shopify", "Shopify"], ["woocommerce", "WooCommerce"]].map(([val, label]) =>
              filterBtn(val, label, platform === val)
            )}

            {/* Date preset */}
            <span style={{ fontSize: 11, color: T.text3, marginLeft: 6 }}>Period:</span>
            <select
              value={datePreset}
              onChange={e => setDatePreset(e.target.value)}
              style={{
                padding: "5px 10px", borderRadius: 8, fontSize: 12, fontWeight: 600,
                background: T.bg0, border: `1px solid ${T.border1}`, color: T.text1,
                cursor: "pointer", outline: "none", fontFamily: base.font,
              }}
            >
              {DATE_PRESETS.map(p => (
                <option key={p.value} value={p.value}>{p.label}</option>
              ))}
            </select>

            <button
              onClick={load}
              style={{
                background: T.bg2, border: `1px solid ${T.border1}`,
                color: T.text2, borderRadius: 8, padding: "5px 12px",
                fontSize: 12, cursor: "pointer", fontFamily: base.font,
              }}
            >
              ↻
            </button>

            {/* Select mode toggle */}
            <button
              onClick={selectMode ? exitSelectMode : enterSelectMode}
              style={{
                background: selectMode ? "rgba(99,102,241,0.15)" : T.bg2,
                border: `1px solid ${selectMode ? "#6366f1" : T.border1}`,
                color: selectMode ? "#6366f1" : T.text2,
                borderRadius: 8, padding: "5px 12px",
                fontSize: 12, fontWeight: 600, cursor: "pointer", fontFamily: base.font,
              }}
            >
              {selectMode ? "Cancel" : "Select"}
            </button>
          </div>
        )}
      </div>

      {/* ── Bulk action toolbar ────────────────────────────────────── */}
      {tab === "pipeline" && selectMode && (
        <div style={{
          display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap",
          padding: "8px 16px", background: "rgba(99,102,241,0.07)",
          borderBottom: `1px solid rgba(99,102,241,0.2)`, flexShrink: 0,
        }}>
          <span style={{ fontSize: 12, fontWeight: 700, color: "#6366f1" }}>
            {selection.size > 0 ? `${selection.size} selected` : "Click cards to select"}
          </span>
          <button
            onClick={() => setSelection(new Set(allOrders.map(o => o._id)))}
            style={{ fontSize: 11, fontWeight: 600, color: T.text3, background: "none", border: "none", cursor: "pointer", padding: "2px 6px" }}>
            Select all ({allOrders.length})
          </button>
          {selection.size > 0 && (
            <button onClick={() => setSelection(new Set())}
              style={{ fontSize: 11, fontWeight: 600, color: T.text3, background: "none", border: "none", cursor: "pointer", padding: "2px 6px" }}>
              Clear
            </button>
          )}
          <div style={{ flex: 1 }} />
          {/* Move to stage */}
          {selection.size > 0 && (
            <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
              <select value={bulkStage} onChange={e => setBulkStage(e.target.value)}
                style={{ padding: "5px 10px", borderRadius: 8, fontSize: 12, background: T.bg0, border: `1px solid ${T.border1}`, color: T.text1, outline: "none", fontFamily: base.font, cursor: "pointer" }}>
                <option value="">Move to stage…</option>
                {STAGES.map(s => <option key={s.id} value={s.id}>{s.label}</option>)}
              </select>
              <button
                onClick={bulkMove}
                disabled={!bulkStage || bulkMoving}
                style={{ padding: "5px 14px", borderRadius: 8, border: "none", background: bulkStage ? "#6366f1" : T.bg2, color: bulkStage ? "#fff" : T.text3, fontSize: 12, fontWeight: 700, cursor: bulkStage ? "pointer" : "not-allowed", fontFamily: base.font, opacity: bulkMoving ? 0.6 : 1 }}>
                {bulkMoving ? "Moving…" : "Move"}
              </button>
              <button
                onClick={bulkDelete}
                disabled={bulkDeleting}
                style={{ padding: "5px 14px", borderRadius: 8, border: "1px solid rgba(244,63,94,0.4)", background: "rgba(244,63,94,0.08)", color: "#f43f5e", fontSize: 12, fontWeight: 700, cursor: "pointer", fontFamily: base.font, opacity: bulkDeleting ? 0.6 : 1 }}>
                {bulkDeleting ? "Deleting…" : `Delete ${selection.size}`}
              </button>
            </div>
          )}
        </div>
      )}

      {tab === "settings" ? (
        <SettingsPanel token={token} showToast={showToast} />
      ) : (
        <>
          {/* ── Stats row ─────────────────────────────────────────── */}
          <div style={{
            display: "flex", gap: 10, padding: "12px 16px",
            background: T.bg1, borderBottom: `1px solid ${T.border1}`, flexShrink: 0,
            overflowX: "auto",
          }}>
            <StatCard label="Total Orders"    value={stats.total}     color={T.text1} />
            <StatCard label="Confirmed"       value={stats.confirmed} color="#22c55e" />
            <StatCard label="Pending"         value={stats.pending}   color="#f59e0b" />
            <StatCard label="Follow-Up"       value={stats.followUp}  color="#38bdf8" />
            <StatCard label="Cancelled"       value={stats.cancelled} color="#f43f5e" />
            <StatCard label="Revenue (conf.)" value={revenueLabel}    color="#22c55e"
              sub={datePreset !== "all" ? DATE_PRESETS.find(p => p.value === datePreset)?.label : ""} />
          </div>

          {/* ── Kanban + Detail ────────────────────────────────────── */}
          <div style={{ display: "flex", flex: 1, overflow: "hidden" }}>
            {/* Kanban board */}
            <div style={{
              flex: 1, display: "flex", gap: 0,
              overflowX: "auto", overflowY: "hidden",
              padding: "14px 10px",
            }}>
              {loading ? (
                <div style={{ flex: 1, display: "flex", alignItems: "center", justifyContent: "center", color: T.text3 }}>
                  <div style={{ width: 28, height: 28, border: `2px solid ${T.border1}`, borderTopColor: "#6366f1", borderRadius: "50%", animation: "spin 0.7s linear infinite" }} />
                </div>
              ) : (
                STAGES.map(stage => {
                  const orders  = stages[stage.id] || [];
                  const isOver  = dragOver === stage.id;
                  return (
                    <div
                      key={stage.id}
                      onDragOver={e => onDragOver(e, stage.id)}
                      onDrop={e => onDrop(e, stage.id)}
                      style={{
                        width: 230, flexShrink: 0, marginRight: 10,
                        display: "flex", flexDirection: "column",
                        background: isOver ? `${stage.color}08` : T.bg1,
                        borderRadius: 14,
                        border: `1px solid ${isOver ? stage.color + "55" : T.border1}`,
                        boxShadow: isOver ? `0 0 0 2px ${stage.color}22` : "none",
                        transition: "all 0.15s",
                      }}
                    >
                      <div style={{
                        padding: "10px 14px", borderBottom: `1px solid ${T.border1}`,
                        display: "flex", alignItems: "center", justifyContent: "space-between",
                        background: `${stage.color}09`,
                      }}>
                        <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                          <span style={{ fontSize: 16 }}>{stage.icon}</span>
                          <span style={{ fontSize: 12, fontWeight: 700, color: stage.color }}>{stage.label}</span>
                        </div>
                        <span style={{
                          fontSize: 11, fontWeight: 700,
                          background: `${stage.color}22`, color: stage.color,
                          borderRadius: 20, padding: "2px 8px",
                        }}>
                          {orders.length}
                        </span>
                      </div>
                      <div style={{ flex: 1, overflowY: "auto", padding: "10px" }}>
                        {orders.length === 0 ? (
                          <div style={{
                            color: isOver ? stage.color : T.text3,
                            fontSize: 12, textAlign: "center", marginTop: 20,
                            border: isOver ? `2px dashed ${stage.color}44` : "2px dashed transparent",
                            borderRadius: 10, padding: "20px 8px", transition: "all 0.15s",
                          }}>
                            {isOver ? "Drop here" : "No orders"}
                          </div>
                        ) : orders.map(order => (
                          <OrderCard
                            key={order._id}
                            order={order}
                            stageColor={stage.color}
                            onSelect={setSelected}
                            selected={selected}
                            onDragStart={() => onDragStart(order, stage.id)}
                            onDragEnd={onDragEnd}
                            isDragging={dragging?.orderId === order._id}
                            selectMode={selectMode}
                            isChecked={selection.has(order._id)}
                            onToggle={() => toggleSelect(order._id)}
                          />
                        ))}
                      </div>
                    </div>
                  );
                })
              )}
            </div>

            {/* Detail panel */}
            {selected && !selectMode && (
              <div style={{
                width: 360, flexShrink: 0,
                background: T.bg1, borderLeft: `1px solid ${T.border1}`,
                display: "flex", flexDirection: "column",
              }}>
                <div style={{
                  padding: "10px 14px", borderBottom: `1px solid ${T.border1}`,
                  display: "flex", justifyContent: "space-between", alignItems: "center",
                }}>
                  <span style={{ fontSize: 12, fontWeight: 700, color: T.text2 }}>ORDER DETAIL</span>
                  <button
                    onClick={() => setSelected(null)}
                    style={{ background: "none", border: "none", color: T.text3, cursor: "pointer", fontSize: 18 }}
                  >
                    ×
                  </button>
                </div>
                <div style={{ flex: 1, display: "flex", flexDirection: "column", minHeight: 0 }}>
                  <OrderDetailPanel
                    order={selected}
                    token={token}
                    showToast={showToast}
                    onStageChange={() => { load(); setSelected(null); }}
                    onDeleted={() => { load(); setSelected(null); }}
                  />
                </div>
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
}
